import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Copy, Download, Image as ImageIcon, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { inr } from "@/lib/api";

const cleanName = (n = "") => n.replace(/\s*Campaign$/i, "").trim();

function buildMessage(c, url, user) {
  const name = cleanName(c.name);
  const payout = c.payout_type === "percent" && c.payout_percent
    ? `${c.payout_percent}% of amount`
    : `${inr(c.payout_pool)} per conversion`;
  // bullets: campaign flow steps agar hain to, warna description ke sentences
  let bullets = (Array.isArray(c.flow) ? c.flow : []).filter(Boolean);
  if (!bullets.length && c.description) {
    bullets = c.description.split(/[.\n]/).map((s) => s.trim()).filter((s) => s.length > 8).slice(0, 4);
  }
  if (!bullets.length) bullets = ["Quick & paperless onboarding", "Instant approval process", "Trusted by thousands"];
  const lines = [
    `📈 *${name}* — Open Today!`,
    ``,
    ...bullets.map((b) => `✅ ${b}`),
    ``,
    `💰 Earn *${payout}*`,
    ``,
    `👉 Apply Now:`,
    url,
    ``,
    `— ${user?.name || "Credit Kendra Partner"}`,
    `✔ Verified Credit Kendra Partner (${user?.user_code || ""})`,
  ];
  return lines.join("\n");
}

// creative URL ko absolute banayo (same-origin /api/files/... ke liye bhi)
function absUrl(src) {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  return `${window.location.origin}${src}`;
}

// Canvas par share-card image compose karo (1080x1350 portrait, WhatsApp-friendly)
async function composeCard(c, user) {
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  // bg gradient (brand navy -> teal)
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, "#0B223D"); g.addColorStop(0.6, "#153654"); g.addColorStop(1, "#0D9488");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  const creative = absUrl(c.desktop_creative || c.mobile_creative);
  let drewImg = false;
  if (creative) {
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = creative;
      });
      // White rounded panel me image ko CONTAIN fit karo — logo/banner kabhi crop nahi hoga
      const px = 60, py = 60, pw = W - 120, ph = Math.round(H * 0.56);
      roundRect(ctx, px, py, pw, ph, 36); ctx.fillStyle = "#ffffff"; ctx.fill();
      const pad = 44;
      const scale = Math.min((pw - pad * 2) / img.width, (ph - pad * 2) / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh);
      drewImg = true;
    } catch (_) { drewImg = false; }
  }

  const textTop = drewImg ? 60 + Math.round(H * 0.56) + 90 : 120;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  // title
  ctx.font = "800 64px Outfit, Manrope, sans-serif";
  wrapText(ctx, cleanName(c.name), 60, textTop, W - 120, 74);
  // payout pill
  const payoutTxt = c.payout_type === "percent" && c.payout_percent ? `Earn ${c.payout_percent}% per lead` : `Earn up to ${inr(c.payout_pool)} per lead`;
  const py = textTop + (cleanName(c.name).length > 22 ? 170 : 100);
  ctx.font = "700 34px Manrope, sans-serif";
  const pw = ctx.measureText(payoutTxt).width + 56;
  roundRect(ctx, 60, py, pw, 64, 32); ctx.fillStyle = "#2563EB"; ctx.fill();
  ctx.fillStyle = "#fff"; ctx.fillText(payoutTxt, 88, py + 44);
  // footer adviser bar
  const fy = H - 150;
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  roundRect(ctx, 40, fy, W - 80, 100, 20); ctx.fill();
  ctx.fillStyle = "#5eead4"; ctx.font = "800 30px Manrope, sans-serif";
  ctx.fillText("✔ Verified Credit Kendra Partner", 70, fy + 42);
  ctx.fillStyle = "#ffffff"; ctx.font = "700 34px Manrope, sans-serif";
  ctx.fillText(`${user?.name || "Partner"}  ·  ${user?.user_code || ""}`, 70, fy + 82);

  return await new Promise((res) => cv.toBlob(res, "image/png", 0.92));
}

function wrapText(ctx, text, x, y, maxW, lh) {
  const words = text.split(" ");
  let line = "", yy = y;
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, x, yy); line = w + " "; yy += lh; }
    else line = test;
  }
  ctx.fillText(line, x, yy);
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

export function ShareCampaignDialog({ campaign, url, user, onClose }) {
  const [cardUrl, setCardUrl] = useState("");
  const [cardBlob, setCardBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const msg = useMemo(() => buildMessage(campaign, url, user), [campaign, url, user]);

  const makeCard = async () => {
    if (cardBlob) return cardBlob;
    setBusy(true);
    try {
      const blob = await composeCard(campaign, user);
      setCardBlob(blob);
      setCardUrl(URL.createObjectURL(blob));
      return blob;
    } catch (_) { toast.error("Card image nahi ban payi"); return null; }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(msg); toast.success("Message copied!"); };
  const whatsappText = () => window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  const downloadCard = async () => {
    const blob = await makeCard();
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${cleanName(campaign.name).replace(/\s+/g, "-")}-share-card.png`;
    a.click();
    toast.success("Card downloaded — WhatsApp me photo attach karke bhejein");
  };
  const shareNative = async () => {
    const blob = await makeCard();
    const file = blob ? new File([blob], "card.png", { type: "image/png" }) : null;
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text: msg, title: cleanName(campaign.name) }); } catch (_) { /* cancelled */ }
    } else if (navigator.share) {
      try { await navigator.share({ text: msg, title: cleanName(campaign.name) }); } catch (_) {}
    } else {
      toast.error("Is browser me direct share nahi — WhatsApp ya Download use karein");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="share-dialog-overlay">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="share-dialog">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-display font-bold text-lg text-[#0B223D] flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-teal-600" /> Share — {cleanName(campaign.name)}
          </h3>
          <button onClick={onClose} data-testid="share-dialog-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* message preview (WhatsApp green bubble style) */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Message Preview</p>
            <div className="bg-[#DCF8C6] rounded-xl rounded-tl-none p-3.5 text-sm text-slate-800 whitespace-pre-wrap font-medium shadow-sm" data-testid="share-message-preview">{msg}</div>
          </div>
          {/* card image preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Share Card (image)</p>
              {!cardUrl && <button onClick={makeCard} disabled={busy} data-testid="generate-card-btn" className="text-xs font-bold text-teal-700 hover:text-teal-800">{busy ? "Generating…" : "Generate card"}</button>}
            </div>
            {cardUrl
              ? <img src={cardUrl} alt="share card" className="w-full rounded-xl border border-slate-200" data-testid="share-card-preview" />
              : <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-center text-xs text-slate-400 flex flex-col items-center gap-2"><ImageIcon className="w-7 h-7 text-slate-300" />Campaign creative + aapka naam/verified badge wala card banega</div>}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button onClick={whatsappText} data-testid="share-wa-btn" className="bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold h-11"><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</Button>
            <Button onClick={shareNative} disabled={busy} data-testid="share-native-btn" className="bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-11"><ImageIcon className="w-4 h-4 mr-1.5" /> Share Card</Button>
            <Button onClick={downloadCard} disabled={busy} variant="outline" data-testid="share-download-btn" className="h-11 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Download Card</Button>
            <Button onClick={copy} variant="outline" data-testid="share-copy-btn" className="h-11 font-semibold"><Copy className="w-4 h-4 mr-1.5" /> Copy Text</Button>
          </div>
          <p className="text-[11px] text-slate-400">WhatsApp par image attach karne ke liye "Share Card" (mobile) ya "Download Card" use karein, phir message ke saath bhejein.</p>
        </div>
      </div>
    </div>,
    document.body
  );
}
